/*
Provides synchronous access to config with async write through to persistent storage.
You must call `await config.init(context = 'default')` first, then you can use the
getter/setter for each config item.
*/

import log from 'modules-core/log'
import storageDatabase from './cache/storageDatabase'
import uuid from './utility/uuid'

const logger = log.getLogger('player')

const createConfig = () => {
	// Allow overriding some config based on query string params.
	const query = new URLSearchParams(location.search)

	// Allow callers to use an existing audio context.
	let _audioContext: AudioContext

	// Set by `config.init()`. Set a different context to use a distinct set of config,
	// e.g. for use on the home page without interfering with the master interface config.
	let _context: string

	// Require `await config.init()` before access.
	let checkContext = () => {
		if (typeof _context === 'undefined') {
			throw Error(
				`Unable to access config. Call 'await syrinscape.config.init()' first.`
			)
		}
	}

	// Local copy of last seen value.
	let localData = {}

	// Get from local data.
	let getLocalItem = (key: string, defaultValue: any = '') => {
		checkContext()
		let value = localData[`${_context}.${key}`]
		if (typeof value === 'undefined') {
			return defaultValue
		}
		return value
	}

	// Get from persistent storage and write to local data.
	let getItem = async (key: string, defaultValue: any = '') => {
		checkContext()
		key = `${_context}.${key}`
		let item = await storageDatabase.getItem(`config.${key}`)
		if (item !== null) {
			localData[key] = JSON.parse(await new Response(item).text())
		} else {
			localData[key] = defaultValue
		}
		return localData[key]
	}

	// Save to persistent storage and write to local data.
	let setItem = async (key: string, value: any) => {
		checkContext()
		key = `${_context}.${key}`
		localData[key] = value
		return await storageDatabase.setItem(
			// Save an empty string instead of `undefined`, which will throw an error when we
			// later try to parse it as JSON.
			`config.${key}`, new Blob([JSON.stringify(value || '')])
		)
	}

	const config = {
		// @ts-ignore injected by webpack
		httpProtocol: query.get('httpProtocol') ?? SITE_PROTOCOL,

		// @ts-ignore injected by webpack
		httpHostname: query.get('httpHostname') ?? SITE_DOMAIN,

		// @ts-ignore injected by webpack
		httpPort: query.get('httpPort') ?? SITE_PORT,

		// @ts-ignore injected by webpack
		wsProtocol: query.get('wsProtocol') ?? SITE_PROTOCOL.replace('http', 'ws'),  // Infer ws or wss from http or https

		// @ts-ignore injected by webpack
		wsHostname: query.get('wsHostname') ?? SITE_DOMAIN,

		// @ts-ignore injected by webpack
		wsPort: query.get('wsPort') ?? SITE_PORT,

		// Token for secure postMessage communication between windows.
		postMessageToken: uuid(), // Disabling as it does not play nice with search

		// postMessage target window.
		postMessageWindow: window.parent !== window ? window.parent : null,

		// Populate local data from persistent storage and set defaults from server.
		init: async (context = 'default', initialConfig = {}) => {
			if (typeof _context !== 'undefined') {
				return
			}
			_context = context
			await Promise.all(
				Object.entries(initialConfig).map(([key, value]) => setItem(key, value))
			)
			let token = await getItem('token')
			// Ideally this would use `authSystem`, but that imports `urlSystem` which
			// imports `config`, which is a circular dependency. So we manually craft an
			// authorised fetch to get a default `sessionId` from the server.
			let url = `${config.httpProtocol}://app.${config.httpHostname}/config/`
			let opts = {
				headers: {
					Accept: 'application/json',
					Authorization: `Token ${token}`,
				}
			}
			let response = await fetch(url, opts)
			if (response.ok) {
				let data = await response.json()
				// Get server, so we can connect directly and avoid being told to reconnect.
				config.wsHostname = data.hostname
				// Get defaults from server.
				let deviceName = await getItem('deviceName')
				if (!deviceName) {
					await setItem('deviceName', data.device_name)
				}
				await setItem('sessionId', data.session_id)
				await setItem('token', data.token)
				// Get the rest from persistent storage.
				await getItem('lastLocalVolume', 1)
				await getItem('localVolume', 1)
				await getItem('visualisationFramerate', 30)
			} else {
				logger.warn(`HTTP ${response.status}: Unable to get config.`)
			}
		},

		// audioContext
		get audioContext(): AudioContext {
			_audioContext ??= new AudioContext()
			return _audioContext
		},
		set audioContext(value: AudioContext) {
			if (typeof _audioContext !== 'undefined') {
				throw Error('An audio context already exists, and cannot be reassigned.')
			}
			_audioContext = value
		},

		// deviceName
		get deviceName() {
			return getLocalItem('deviceName')
		},
		set deviceName(value) {
			setItem('deviceName', value)
		},

		// lastLocalVolume
		get lastLocalVolume() {
			return getLocalItem('lastLocalVolume')
		},
		set lastLocalVolume(value) {
			if (Number(value) === NaN) {
				throw Error(`Invalid lastLocalVolume: ${value}`)
			}
			setItem('lastLocalVolume', value)
		},

		// localVolume
		get localVolume() {
			return getLocalItem('localVolume')
		},
		set localVolume(value) {
			if (Number(value) === NaN) {
				throw Error(`Invalid localVolume: ${value}`)
			}
			setItem('localVolume', value)
		},

		// sessionId
		get sessionId() {
			return getLocalItem('sessionId')
		},
		set sessionId(value) {
			setItem('sessionId', value)
		},

		// token
		get token() {
			return getLocalItem('token')
		},
		set token(value) {
			setItem('token', value)
		},

		// visualisationFramerate
		get visualisationFramerate() {
			return getLocalItem('visualisationFramerate')
		},
		set visualisationFramerate(value) {
			if (typeof value !== 'number' || value <= 0) {
				throw Error(`Invalid visualisationFramerate: ${value}`)
			}
			setItem('visualisationFramerate', value)
		},
	}

	return config
}

export const config = createConfig()
export default config
